import React, { useState, useEffect } from 'react';
import * as S from './AdvancesStyles';
import { toast } from 'react-toastify';
import Select from 'react-select';
import useEmployeeIds from './useEmployeeIds';

// Apiary and Non-Apiary expenses validate against different category enums on the
// backend (ExpenseModel vs NonApiaryExpenseModel) — using the wrong list for a given
// Tagged To selection would fail server-side validation, not just look wrong.
const APIARY_CATS = ['Sugar', 'Medicine', 'Labour', 'Transport', 'Maintenance', 'Other'];
const NON_APIARY_CATS = ['Travel', 'Food', 'Stay', 'Office', 'Misc'];
const CATS = APIARY_CATS;

const AddEntryModal = ({
  show,
  members,
  apiaries,
  initialType = 'expense',
  initialMemberId = '',
  onClose,
  onSave
}) => {
  const [entryType, setEntryType] = useState(initialType);
  const [memberId, setMemberId] = useState(initialMemberId);
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [category, setCategory] = useState(CATS[0]);
  const [tagType, setTagType] = useState('apiary');
  const [locId, setLocId] = useState('');
  const [comment, setComment] = useState('');
  const empIdFor = useEmployeeIds();

  useEffect(() => {
    setEntryType(initialType || 'expense');
    setMemberId(initialMemberId || (members.length ? members[0].id : ''));
    setAmount('');
    setDate(new Date().toISOString().split('T')[0]);
    setCategory(CATS[0]);
    setTagType('apiary');
    setLocId(apiaries.length ? apiaries[0].id : '');
    setComment('');
  }, [show, initialType, initialMemberId, members, apiaries]);

  // "Name (EMP ID) - mode" label, searchable by name, employee ID or mode.
  const memberOptions = members.map(m => {
    const emp = empIdFor(m.id);
    return {
      value: m.id,
      label: `${m.name}${emp ? ` (${emp})` : ''} - ${m.mode}`,
      searchText: `${m.name || ''} ${emp} ${m.mode || ''}`.toLowerCase()
    };
  });

  if (!show) return null;

  const handleTagTypeChange = newTagType => {
    setTagType(newTagType);
    // Switching category lists — reset to that list's first option so a category from
    // the other enum can never end up submitted for the wrong expense type.
    setCategory(newTagType === 'non_apiary' ? NON_APIARY_CATS[0] : APIARY_CATS[0]);
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!amount || Number(amount) <= 0) {
      toast.error('Please enter a valid amount!');
      return;
    }
    if (!memberId) {
      toast.error('Please select a team member!');
      return;
    }

    const payload = {
      type: entryType,
      memberId,
      amount: Number(amount),
      date,
      category: entryType === 'expense' ? category.toUpperCase() : undefined,
      tagType: entryType === 'expense' ? tagType : undefined,
      locId: entryType === 'expense' ? (tagType !== 'non_apiary' ? locId : null) : (locId || null),
      comment
    };

    onSave(payload);
    onClose();
  };

  return (
    <S.ModalOverlay show={show}>
      <S.ModalContent>
        <h2>Add Entry</h2>
        <div
          style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '18px',
            background: '#F4F5F7',
            padding: '5px',
            borderRadius: '10px'
          }}
        >
          <button
            type="button"
            style={{
              flex: 1,
              all: 'unset',
              textAlign: 'center',
              padding: '9px 6px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
              background: entryType === 'expense' ? '#fff' : 'transparent',
              color: entryType === 'expense' ? '#CE4A1E' : '#6B7280',
              boxShadow: entryType === 'expense' ? '0 1px 3px rgba(0,0,0,.1)' : 'none'
            }}
            onClick={() => setEntryType('expense')}
          >
            Log Expense
          </button>
          <button
            type="button"
            style={{
              flex: 1,
              all: 'unset',
              textAlign: 'center',
              padding: '9px 6px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
              background: entryType === 'advance_issued' ? '#fff' : 'transparent',
              color: entryType === 'advance_issued' ? '#CE4A1E' : '#6B7280',
              boxShadow: entryType === 'advance_issued' ? '0 1px 3px rgba(0,0,0,.1)' : 'none'
            }}
            onClick={() => setEntryType('advance_issued')}
          >
            Issue Advance
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px'
            }}
          >
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: '11px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', display: 'block', marginBottom: '5px' }}>
                Team Member
              </label>
              <Select
                options={memberOptions}
                value={memberOptions.find(o => String(o.value) === String(memberId)) || null}
                onChange={opt => setMemberId(opt ? opt.value : '')}
                filterOption={(option, input) => option.data.searchText.includes((input || '').trim().toLowerCase())}
                placeholder="Search by name or employee ID"
                maxMenuHeight={220}
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }), control: base => ({ ...base, fontSize: '13px' }) }}
              />
            </div>

            <div>
              <label style={{ fontSize: '11px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', display: 'block', marginBottom: '5px' }}>
                Amount (₹)
              </label>
              <input
                type="number"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="e.g. 5000"
                style={{ width: '100%', padding: '9px 10px', borderRadius: '8px', border: '1px solid #E7E8EC', fontSize: '13px' }}
                required
              />
            </div>

            <div>
              <label style={{ fontSize: '11px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', display: 'block', marginBottom: '5px' }}>
                Date
              </label>
              <input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                style={{ width: '100%', padding: '9px 10px', borderRadius: '8px', border: '1px solid #E7E8EC', fontSize: '13px' }}
                required
              />
            </div>

            {entryType === 'expense' && (
              <>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', display: 'block', marginBottom: '5px' }}>
                    Category
                  </label>
                  <select
                    value={category}
                    onChange={e => setCategory(e.target.value)}
                    style={{ width: '100%', padding: '9px 10px', borderRadius: '8px', border: '1px solid #E7E8EC', fontSize: '13px' }}
                  >
                    {(tagType === 'non_apiary' ? NON_APIARY_CATS : APIARY_CATS).map(c => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', display: 'block', marginBottom: '5px' }}>
                    Tagged To
                  </label>
                  <select
                    value={tagType}
                    onChange={e => handleTagTypeChange(e.target.value)}
                    style={{ width: '100%', padding: '9px 10px', borderRadius: '8px', border: '1px solid #E7E8EC', fontSize: '13px' }}
                  >
                    <option value="apiary">Apiary</option>
                    <option value="non_apiary">Non-apiary / General</option>
                  </select>
                </div>

                {tagType === 'apiary' && (
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', display: 'block', marginBottom: '5px' }}>
                      Location
                    </label>
                    <select
                      value={locId}
                      onChange={e => setLocId(e.target.value)}
                      style={{ width: '100%', padding: '9px 10px', borderRadius: '8px', border: '1px solid #E7E8EC', fontSize: '13px' }}
                    >
                      {apiaries.map(a => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            )}

            {entryType === 'advance_issued' && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', display: 'block', marginBottom: '5px' }}>
                  Linked Apiary (Optional)
                </label>
                <select
                  value={locId}
                  onChange={e => setLocId(e.target.value)}
                  style={{ width: '100%', padding: '9px 10px', borderRadius: '8px', border: '1px solid #E7E8EC', fontSize: '13px' }}
                >
                  <option value="">Independent / None</option>
                  {apiaries.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: '11px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', display: 'block', marginBottom: '5px' }}>
                Comments / Note
              </label>
              <textarea
                value={comment}
                onChange={e => setComment(e.target.value)}
                rows={2}
                placeholder="Optional notes or receipt references..."
                style={{ width: '100%', padding: '9px 10px', borderRadius: '8px', border: '1px solid #E7E8EC', fontSize: '13px' }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
            <S.Btn type="button" ghost onClick={onClose}>
              Cancel
            </S.Btn>
            <S.Btn type="submit">Save Entry</S.Btn>
          </div>
        </form>
      </S.ModalContent>
    </S.ModalOverlay>
  );
};

export default AddEntryModal;
